// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * LES TABLES DE CORRESPONDANCE PATIENT (Ines, 31/10/2026).
 *
 * « Nous mentionnons le vocabulaire médical dans le référentiel et il y a une
 * table de correspondance pour ce qui sera affiché au patient. »
 *
 * Le référentiel de Nafis garde son vocabulaire de laboratoire, en codes
 * fermés : milieu `sang_veineux`, contenant `edta`, jeûne `requis` + 12 h.
 * Ce que le PATIENT lit (« Sang », « Tube violet », « À jeun : ne rien manger
 * depuis 12 h… ») et la couleur du bouchon se règlent dans Nafis › Référentiel
 * › Paramétrage › Correspondances patient, et sont stockés dans
 * `field-config.correspondancesPatient`.
 *
 * Tant que rien n'est enregistré (`null`), ou pour un code que la table ne
 * porte pas encore, ce sont les valeurs ci-dessous qui s'appliquent — celles
 * que le catalogue et le site affichaient jusqu'ici, écrites dans leur code.
 * Elles ne servent plus que de point de départ.
 *
 * Module PUR, recopié dans site, manuel et ged par `bash shared/sync.sh`.
 * ⚠️ Le moteur applique les mêmes bornes (`backend/src/correspondances-patient.ts`).
 */

export interface ContenantPatient {
  /** Ce que le patient lit (« Tube violet »). Vide = ne pas le montrer au patient. */
  patient: string;
  /** Couleur du bouchon ou de la pastille, `#rrggbb`. */
  couleur: string;
}

export interface PhrasesJeune {
  /** À jeun, avec une durée : `{duree}` est remplacé par « 12 h » ou « 8 à 12 h ». */
  requis: string;
  /** À jeun, sans durée réglée. */
  requisSansDuree: string;
  recommande: string;
  aucun: string;
}

export interface CorrespondancesPatient {
  /** Code du milieu prélevé (`fiche.milieux`) → mot du patient. Vide = ne pas le montrer. */
  matieres: Record<string, string>;
  /** Code du contenant (`recipient`) → mot du patient et couleur. */
  contenants: Record<string, ContenantPatient>;
  jeune: PhrasesJeune;
}

export const CORRESPONDANCES_DEFAUT: CorrespondancesPatient = {
  matieres: {
    sang_veineux: 'Sang',
    sang_arteriel: 'Sang artériel',
    sang_capillaire: 'Sang (piqûre au doigt)',
    urine: 'Urine',
    selles: 'Selles',
    sperme: 'Sperme',
    expectoration: 'Crachat',
    lcr: 'Liquide céphalo-rachidien',
    ponction: 'Liquide de ponction',
    prelevement_genital: 'Prélèvement génital',
    prelevement_orl: 'Prélèvement ORL (nez, gorge)',
    prelevement_cutane: 'Prélèvement de peau ou de plaie',
    biopsie: 'Biopsie',
    // « Autre milieu » ne dit rien au patient : mieux vaut ne rien afficher.
    autre: '',
  },
  contenants: {
    edta: { patient: 'Tube violet', couleur: '#7E3FF2' },
    citrate: { patient: 'Tube bleu', couleur: '#2563EB' },
    sec: { patient: 'Tube rouge', couleur: '#DC2626' },
    gel: { patient: 'Tube jaune', couleur: '#E0A615' },
    heparine: { patient: 'Tube vert', couleur: '#16A34A' },
    fluorure: { patient: 'Tube gris', couleur: '#6B7280' },
    hemoc: { patient: 'Flacon d’hémoculture', couleur: '#0D9488' },
    ecouvillon: { patient: 'Écouvillon', couleur: '#EC4899' },
    urine: { patient: 'Flacon à urine', couleur: '#38BDF8' },
    selles: { patient: 'Pot à selles', couleur: '#92633A' },
    sterile: { patient: 'Flacon stérile', couleur: '#64748B' },
    expecto: { patient: 'Pot à crachat', couleur: '#B45309' },
    seringue: { patient: 'Seringue', couleur: '#065F46' },
    lcr: { patient: 'Tube de ponction lombaire', couleur: '#A21CAF' },
    biopsie: { patient: 'Pot à biopsie', couleur: '#7C2D12' },
    cytobrosse: { patient: 'Brosse de prélèvement', couleur: '#BE185D' },
  },
  jeune: {
    requis: 'À jeun : ne rien manger depuis {duree}, l’eau est permise',
    requisSansDuree: 'À jeun : ne rien manger avant le prélèvement, l’eau est permise',
    recommande: 'À jeun de préférence',
    aucun: 'Pas besoin d’être à jeun',
  },
};

/** Les niveaux de jeûne du référentiel (`preanalytique.jeune`), dits au laboratoire. */
export const NIVEAUX_JEUNE: Record<'requis' | 'recommande' | 'aucun', string> = {
  requis: 'À jeun obligatoire',
  recommande: 'À jeun recommandé',
  aucun: 'Sans jeûne',
};

export const BORNES_CORRESPONDANCES = { texte: 160, codes: 80 } as const;

const estObjet = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** La table en vigueur : le paramétrage, code par code, par-dessus les valeurs
 *  de départ. Une valeur mal formée est ignorée, jamais propagée. */
export function correspondancesEnVigueur(parametres: unknown): CorrespondancesPatient {
  const p = estObjet(parametres) ? parametres : {};
  const matieres = { ...CORRESPONDANCES_DEFAUT.matieres };
  if (estObjet(p.matieres)) {
    for (const [c, v] of Object.entries(p.matieres)) if (typeof v === 'string') matieres[c] = v.trim();
  }
  const contenants: Record<string, ContenantPatient> = {};
  for (const [c, v] of Object.entries(CORRESPONDANCES_DEFAUT.contenants)) contenants[c] = { ...v };
  if (estObjet(p.contenants)) {
    for (const [c, v] of Object.entries(p.contenants)) {
      if (!estObjet(v)) continue;
      const base = contenants[c] || { patient: '', couleur: '#64748B' };
      contenants[c] = {
        patient: typeof v.patient === 'string' ? v.patient.trim() : base.patient,
        couleur: typeof v.couleur === 'string' && /^#[0-9a-fA-F]{6}$/.test(v.couleur) ? v.couleur : base.couleur,
      };
    }
  }
  const jeune = { ...CORRESPONDANCES_DEFAUT.jeune };
  if (estObjet(p.jeune)) {
    for (const k of Object.keys(jeune) as (keyof PhrasesJeune)[]) {
      const v = p.jeune[k];
      if (typeof v === 'string' && v.trim()) jeune[k] = v.trim();
    }
  }
  return { matieres, contenants, jeune };
}

/** Contrôle avant écriture — le moteur applique les mêmes bornes. */
export function erreurCorrespondances(v: unknown): string | null {
  if (!estObjet(v)) return 'La table doit être un objet.';
  const code = (c: string) => /^[a-z0-9_-]{1,40}$/.test(c);
  if (v.matieres !== undefined) {
    if (!estObjet(v.matieres)) return 'Matières : table attendue.';
    const e = Object.entries(v.matieres);
    if (e.length > BORNES_CORRESPONDANCES.codes) return 'Matières : trop de codes.';
    for (const [c, t] of e) {
      if (!code(c)) return `Matières : code invalide « ${c} ».`;
      if (typeof t !== 'string' || t.length > BORNES_CORRESPONDANCES.texte) return `Matières : libellé de « ${c} » invalide ou trop long.`;
    }
  }
  if (v.contenants !== undefined) {
    if (!estObjet(v.contenants)) return 'Contenants : table attendue.';
    const e = Object.entries(v.contenants);
    if (e.length > BORNES_CORRESPONDANCES.codes) return 'Contenants : trop de codes.';
    for (const [c, t] of e) {
      if (!code(c)) return `Contenants : code invalide « ${c} ».`;
      if (!estObjet(t)) return `Contenants : « ${c} » mal formé.`;
      if (typeof t.patient !== 'string' || t.patient.length > BORNES_CORRESPONDANCES.texte) return `Contenants : libellé de « ${c} » invalide ou trop long.`;
      if (typeof t.couleur !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(t.couleur)) return `Contenants : couleur de « ${c} » invalide.`;
    }
  }
  if (v.jeune !== undefined) {
    if (!estObjet(v.jeune)) return 'Jeûne : table attendue.';
    for (const k of Object.keys(CORRESPONDANCES_DEFAUT.jeune)) {
      const t = v.jeune[k];
      if (typeof t !== 'string' || !t.trim() || t.length > BORNES_CORRESPONDANCES.texte) return `Jeûne : phrase « ${k} » vide ou trop longue.`;
    }
    if (!String(v.jeune.requis).includes('{duree}')) return 'Jeûne : la phrase « à jeun avec durée » doit contenir {duree}.';
  }
  return null;
}

/* ── LE JEÛNE STRUCTURÉ (`preanalytique.jeune`, `jeuneHeures`, `jeuneHeuresMin`) ── */

export type NiveauJeune = 'requis' | 'recommande' | 'aucun';
export interface JeuneStructure { niveau: NiveauJeune; heures: number | null; heuresMin: number | null }

const heures = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) && n > 0 && n <= 24 ? n : null;
};

/** Le jeûne réglé dans le référentiel, ou null s'il ne l'est pas. */
export function lireJeune(preanalytique: unknown): JeuneStructure | null {
  if (!estObjet(preanalytique)) return null;
  const n = preanalytique.jeune;
  if (n !== 'requis' && n !== 'recommande' && n !== 'aucun') return null;
  const h = n === 'requis' ? heures(preanalytique.jeuneHeures) : null;
  const min = h !== null ? heures(preanalytique.jeuneHeuresMin) : null;
  return { niveau: n, heures: h, heuresMin: min !== null && min < h! ? min : null };
}

const nombre = (h: number) => String(h).replace('.', ',');

/** « 12 h », « 8 à 12 h », ou '' sans durée. */
export function dureeJeune(j: JeuneStructure): string {
  if (j.heures === null) return '';
  return j.heuresMin !== null ? `${nombre(j.heuresMin)} à ${nombre(j.heures)} h` : `${nombre(j.heures)} h`;
}

/** La phrase du patient, d'après la table de correspondance. */
export function phraseJeune(j: JeuneStructure, table: CorrespondancesPatient = CORRESPONDANCES_DEFAUT): string {
  if (j.niveau === 'aucun') return table.jeune.aucun;
  if (j.niveau === 'recommande') return table.jeune.recommande;
  const d = dureeJeune(j);
  return d ? table.jeune.requis.replace('{duree}', d) : table.jeune.requisSansDuree;
}

/** Les matières d'un examen, en mots de patient, dans l'ordre saisi et sans
 *  doublon. Un code sans libellé patient est écarté. */
export function matieresPatient(milieux: unknown, table: CorrespondancesPatient = CORRESPONDANCES_DEFAUT): string[] {
  const codes = Array.isArray(milieux) ? milieux.map((m) => String(m)) : [];
  const vus = new Set<string>();
  const out: string[] = [];
  for (const c of codes) {
    const libelle = table.matieres[c];
    if (!libelle || vus.has(libelle)) continue;
    vus.add(libelle);
    out.push(libelle);
  }
  return out;
}
